import { connect } from 'react-redux';
import { get, isEmpty } from 'lodash';
import i18next from 'i18next';
import moment from 'moment-timezone';
import React, { useState, useEffect } from 'react';

// MUI
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { Box, IconButton, TableBody, TableHead, TableRow } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

// Actions
import { loading, loadingSuccess } from '@actions/loading';
import { openGenericModal } from '@actions/modal';
import { showErrorMessage, showSuccessMessage } from '@actions/messageconfirmation';

// Common
import { GenericCancelButton, GenericSaveButton } from '@commons/Buttons';
import { TIME_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import Text, { ENUM_COLORS } from '@commons/Text';

import storeService from '@services/store';

// Design
import {
  ActionButton,
  NameCell,
  StyledTable,
  StyledTableContainer,
  StyledTableHeaderCell,
  StyledTableRow,
  StyledTextField,
  StyledTimePicker,
  TimeSlotCell,
  actionBarStyle,
  timePickerPopperStyle,
} from './styledMUIComponents';
import {
  CloseButton,
  Container,
  ContentContainer,
  FooterContainer,
  HeaderContainer,
  Title,
} from './styledComponents';

import { getDeleteTimeSlotModalParams } from './modals';
import { sortTimeSlots, doSlotsOverlap } from './utils';

const initialTimeSlots = [
  {
    id: 'initial-time-slot-1',
    name: null,
    startTime: null,
    endTime: null,
    active: true,
    newTimeSlot: true,
  },
  {
    id: 'initial-time-slot-2',
    name: null,
    startTime: null,
    endTime: null,
    active: true,
    newTimeSlot: true,
  },
];

const TimeSlotManagement = (props) => {
  const {
    // From redux
    showErrorMessage,
    showSuccessMessage,
    pageLoading,
    pageLoaded,
    openGenericModal,

    user,
    storeName,
    storeId,
    storeTimeSlots,
    onClose,
  } = props;

  /**********
   * STATES *
   **********/
  const [userLanguageCode] = useState(get(user, 'lnkLanguageAccountrel.code', 'fr'));

  // Local state for time slots
  const [timeSlots, setTimeSlots] = useState(initialTimeSlots);
  const [isLoading, setIsLoading] = useState(false);

  const [timeSlotInputErrors, setTimeSlotInputErrors] = useState({});
  const [overlapErrors, setOverlapErrors] = useState(new Set());
  const [deletedTimeSlots, setDeletedTimeSlots] = useState([]);

  /*************
   * EFFECTS *
   *************/

  useEffect(() => {
    if (!!storeTimeSlots.length) {
      setTimeSlots(storeTimeSlots);
      return;
    }

    setTimeSlots(initialTimeSlots);
  }, [storeTimeSlots]);

  // Check for overlaps whenever timeSlots change
  useEffect(() => {
    checkForOverlaps(timeSlots);
  }, [timeSlots]);

  /*************
   * HANDLERS *
   *************/

  const updateTimeSlot = (id, field, value) => {
    const updatedTimeSlots = timeSlots.map((slot) =>
      slot.id === id ? { ...slot, [field]: value } : slot,
    );

    const sortedTimeSlots = updatedTimeSlots.sort(sortTimeSlots);

    setTimeSlots(sortedTimeSlots);
  };

  const addTimeSlot = () => {
    const newSlot = {
      id: `new-time-slot-${Date.now()}`, // having an id is necessary for the MUI Table
      name: null,
      startTime: null,
      endTime: null,
      active: true,
      newTimeSlot: true, // Used to identify new time slots
    };
    setTimeSlots((prev) => [...prev, newSlot]);
  };

  const removeTimeSlot = (slot) => {
    setTimeSlots((prev) => prev.filter((s) => s.id !== slot.id));

    if (!slot.newTimeSlot) {
      // If the slot is not one recently added, add it to the soon-to-be deactivated time slots
      setDeletedTimeSlots((prev) => [
        ...prev,
        {
          ...slot,
          active: false,
        },
      ]);
    }
  };

  const handleDeleteIconClick = (slot) => {
    if (!slot.id || slot.id.includes('new-time-slot') || slot.id.includes('initial-time-slot')) {
      removeTimeSlot(slot);
      return;
    }

    openGenericModal(
      getDeleteTimeSlotModalParams({
        handleDelete: () => removeTimeSlot(slot),
      }),
    );
  };

  // Function to check for overlapping time slots
  const checkForOverlaps = (slots) => {
    const overlappingIds = new Set();

    // Filter out slots without both start and end times
    const validSlots = slots.filter((slot) => slot.startTime && slot.endTime);

    for (let i = 0; i < validSlots.length; i++) {
      for (let j = i + 1; j < validSlots.length; j++) {
        const slot1 = validSlots[i];
        const slot2 = validSlots[j];

        // Check for overlap: slot1 starts before slot2 ends AND slot1 ends after slot2 starts
        const overlap = doSlotsOverlap(slot1, slot2);

        if (overlap) {
          overlappingIds.add(slot1.id);
          overlappingIds.add(slot2.id);
        }
      }
    }

    setOverlapErrors(overlappingIds);
  };

  const formatTimeSlot = (timeSlot) => ({
    storeId,
    name: timeSlot.name,
    // check if startTime is a moment object, if it is, format it
    startTime: moment(timeSlot.startTime).isValid()
      ? moment(timeSlot.startTime).format(TIME_DISPLAY_FORMATS.SLASHED_HOUR_MINUTE)
      : timeSlot.startTime,
    endTime: moment(timeSlot.endTime).isValid()
      ? moment(timeSlot.endTime).format(TIME_DISPLAY_FORMATS.SLASHED_HOUR_MINUTE)
      : timeSlot.endTime,
    active: timeSlot.active,
  });

  const saveTimeSlots = async () => {
    setIsLoading(true);
    pageLoading();

    try {
      const timeSlotsPayload = timeSlots.map((slot) => {
        if (slot.newTimeSlot) {
          return formatTimeSlot(slot);
        }

        return { ...formatTimeSlot(slot), id: slot.id };
      });
      timeSlotsPayload.push(...deletedTimeSlots);

      await storeService.updateStoreTimeSlots(storeId, timeSlotsPayload);

      showSuccessMessage(i18next.t('ADMIN.STORES.TIME_SLOTS_UPDATED'));
      onClose({ needRefresh: true });
    } catch (error) {
      showErrorMessage(i18next.t('ADMIN.STORES.TIME_SLOTS_UPDATE_ERROR'));
    } finally {
      setIsLoading(false);
      pageLoaded();
    }
  };

  const resetStateAndClose = () => {
    if (storeTimeSlots.length > 0) {
      setTimeSlots(storeTimeSlots);
    } else {
      // Assigning unique IDs using timestamps to force React to re-render updated initial time slots,
      // since reusing the same initialTimeSlots IDs would prevent the UI from refreshing as expected.
      setTimeSlots([
        { ...initialTimeSlots[0], id: `initial-time-slot-1-${Date.now()}` },
        { ...initialTimeSlots[1], id: `initial-time-slot-2-${Date.now()}` },
      ]);
    }

    onClose();
  };

  // See https://v6.mui.com/x/api/date-pickers/time-picker/#time-picker-prop-onError for reference
  const onTimeSlotInputErrors = (slotId, error, field) => {
    const updatedTimeSlotInputErrors = { ...timeSlotInputErrors };

    if (error) {
      updatedTimeSlotInputErrors[`${slotId}-${field}`] = error;
      setTimeSlotInputErrors(updatedTimeSlotInputErrors);
    } else {
      delete updatedTimeSlotInputErrors[`${slotId}-${field}`];
      setTimeSlotInputErrors(updatedTimeSlotInputErrors);
    }
  };

  /**
   * Check if the form is valid
   * Error cases:
   * 1. timeSlotInputErrors is whether at least one time slot is incorrect, ex: the startTime is after the endTime, or the input is invalid (05:mm).
   * 2. Only one time slot is present: this does not make sense, it comes back to having the default time slot.
   * 3. There are overlapping time slot, i.e the endTime of a time slot is after the startTime of another.
   * 4. At least one time slot is missing either name, startTime or endTime.
   */
  const isFormValid = () => {
    if (!isEmpty(timeSlotInputErrors) || timeSlots.length === 1 || overlapErrors.size > 0) {
      return false;
    }

    return timeSlots.every((slot) => slot.name && slot.startTime && slot.endTime);
  };

  return (
    <Container>
      <HeaderContainer>
        <CloseButton
          alt="close"
          src="/images/inpulse/close-black-small.svg"
          onClick={resetStateAndClose}
        />
        <Title>{i18next.t('ADMIN.STORES.MANAGE_TIME_SLOTS_ACTIONS')}</Title>
        <Text color={ENUM_COLORS.IP_BLACK_4}>{storeName}</Text>
      </HeaderContainer>

      <ContentContainer>
        <Box sx={{ width: '100%' }}>
          <StyledTableContainer>
            <StyledTable aria-label="time slots table">
              <TableHead>
                <TableRow>
                  <StyledTableHeaderCell>{i18next.t('GENERAL.NAME')}</StyledTableHeaderCell>
                  <StyledTableHeaderCell>{i18next.t('GENERAL.TIME_SLOT')}</StyledTableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {timeSlots.sort(sortTimeSlots).map((slot) => (
                  <StyledTableRow key={slot.id}>
                    <NameCell>
                      <StyledTextField
                        placeholder={i18next.t('GENERAL.INPUT_PLACEHOLDER')}
                        size="small"
                        value={slot.name}
                        variant="outlined"
                        fullWidth
                        onChange={(e) => updateTimeSlot(slot.id, 'name', e.target.value)}
                      />
                    </NameCell>
                    <TimeSlotCell>
                      <LocalizationProvider
                        adapterLocale={userLanguageCode}
                        dateAdapter={AdapterMoment}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <StyledTimePicker
                            hasOverlapError={overlapErrors.has(slot.id)}
                            slotProps={{
                              actionBar: {
                                sx: actionBarStyle,
                              },
                              popper: {
                                sx: timePickerPopperStyle,
                              },
                            }}
                            value={
                              slot.startTime
                                ? moment(slot.startTime, TIME_DISPLAY_FORMATS.SLASHED_HOUR_MINUTE)
                                : null
                            }
                            onChange={(newTime) => updateTimeSlot(slot.id, 'startTime', newTime)}
                            onError={(error, value) =>
                              onTimeSlotInputErrors(slot.id, error, 'startTime')
                            }
                          />
                          -
                          <StyledTimePicker
                            hasOverlapError={overlapErrors.has(slot.id)}
                            minTime={
                              slot.startTime
                                ? moment(
                                    slot.startTime,
                                    TIME_DISPLAY_FORMATS.SLASHED_HOUR_MINUTE,
                                  ).add(5, 'minutes')
                                : null
                            }
                            slotProps={{
                              actionBar: {
                                sx: actionBarStyle,
                              },
                              popper: {
                                sx: timePickerPopperStyle,
                              },
                            }}
                            value={
                              slot.endTime
                                ? moment(slot.endTime, TIME_DISPLAY_FORMATS.SLASHED_HOUR_MINUTE)
                                : null
                            }
                            onChange={(newTime) => updateTimeSlot(slot.id, 'endTime', newTime)}
                            onError={(error, value) =>
                              onTimeSlotInputErrors(slot.id, error, 'endTime')
                            }
                          />
                          <IconButton size="small" onClick={() => handleDeleteIconClick(slot)}>
                            <img src="/images/inpulse/delete-ip-black-small.svg" />
                          </IconButton>
                        </Box>
                      </LocalizationProvider>
                    </TimeSlotCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </StyledTable>
          </StyledTableContainer>

          <Box sx={{ justifySelf: 'left' }}>
            <ActionButton
              startIcon={<img src="/images/inpulse/add-black-small.svg" />}
              variant="text"
              onClick={addTimeSlot}
            >
              {i18next.t('ADMIN.STORES.ADD_TIME_SLOT')}
            </ActionButton>
          </Box>
        </Box>
      </ContentContainer>

      {overlapErrors.size > 0 && (
        <Box sx={{ padding: '12px 0' }}>
          <Text color={ENUM_COLORS.INFO_RED}>
            {i18next.t('ADMIN.STORES.TIME_SLOTS_OVERLAP_ERROR')}
          </Text>
        </Box>
      )}

      <FooterContainer>
        <GenericCancelButton handleClick={resetStateAndClose} minWidth={160} />
        <GenericSaveButton
          handleClick={saveTimeSlots}
          isDisabled={isLoading || !isFormValid()}
          minWidth={160}
        />
      </FooterContainer>
    </Container>
  );
};

const mapStateToProps = (state) => ({
  user: state.baseReducer.user,
});

const mapDispatchToProps = (dispatch) => ({
  showSuccessMessage: (message) => {
    dispatch(showSuccessMessage(message));
  },
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
  pageLoading: () => {
    dispatch(loading());
  },
  pageLoaded: () => {
    dispatch(loadingSuccess());
  },
  openGenericModal: (params) => {
    dispatch(openGenericModal(params));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(TimeSlotManagement);
